import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
  getCargosControllerListarQueryKey,
  useCargosControllerCatalogo,
  useCargosControllerDefinirPermissoes,
} from '@/api/generated/admin-cargos/admin-cargos';
import type { CargoResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { EstadoCarregando, EstadoErro } from '@/shared/components/estados';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Separator } from '@/shared/ui/separator';
import { toast } from '@/shared/ui/sonner';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  cargo: CargoResponse;
}

/**
 * Marca as permissões de um cargo, agrupadas por módulo.
 *
 * O agrupamento e os rótulos vêm do servidor (`moduloLabel`, `acaoLabel`, que
 * já existem na tabela `permissoes`) — a tela não mantém dicionário próprio,
 * senão viraria uma segunda fonte de verdade para divergir da primeira.
 *
 * Envia o conjunto COMPLETO (PUT), não um diff: com duas abas abertas, um diff
 * dependeria de as duas concordarem sobre o estado anterior.
 */
export function PermissoesDialogo({ aberto, aoFechar, cargo }: Props) {
  const queryClient = useQueryClient();
  const { data, isPending, isError, refetch } = useCargosControllerCatalogo({
    query: { enabled: aberto },
  });
  const definir = useCargosControllerDefinirPermissoes();

  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (aberto) {
      setMarcadas(new Set(cargo.permissoes));
      setErro(null);
    }
  }, [aberto, cargo]);

  const porEscopo = useMemo(() => {
    const modulos = data?.modulos ?? [];
    return {
      CLINICA: modulos.filter((m) => m.escopo === 'CLINICA'),
      ADMIN: modulos.filter((m) => m.escopo === 'ADMIN'),
    };
  }, [data]);

  function alternar(chave: string) {
    setMarcadas((atual) => {
      const proxima = new Set(atual);
      if (proxima.has(chave)) proxima.delete(chave);
      else proxima.add(chave);
      return proxima;
    });
  }

  function alternarModulo(chaves: string[], marcarTodas: boolean) {
    setMarcadas((atual) => {
      const proxima = new Set(atual);
      for (const c of chaves) {
        if (marcarTodas) proxima.add(c);
        else proxima.delete(c);
      }
      return proxima;
    });
  }

  async function salvar() {
    setErro(null);
    try {
      await definir.mutateAsync({ id: cargo.id, data: { permissoes: [...marcadas] } });
      await queryClient.invalidateQueries({ queryKey: getCargosControllerListarQueryKey() });
      // A revogação vale no request seguinte: o backend invalida o cache de
      // permissões no commit.
      toast.success('Permissões atualizadas.');
      aoFechar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível salvar as permissões.');
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Permissões · {cargo.nome}</DialogTitle>
          <DialogDescription>
            {marcadas.size} de {data?.total ?? 0} permissões marcadas.
            {cargo.totalMembros > 0 &&
              ` Afeta ${cargo.totalMembros} usuário(s) já no request seguinte.`}
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <EstadoCarregando rotulo="Carregando portfólio..." />
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : (
          <div className="space-y-6">
            {erro && (
              <p className="text-sm text-destructive" role="alert">
                {erro}
              </p>
            )}

            {(['CLINICA', 'ADMIN'] as const).map((escopo) => (
              <section key={escopo} className="space-y-3">
                <h3 className="text-xs font-semibold text-brand uppercase tracking-wide">
                  {escopo === 'CLINICA' ? 'Clínico' : 'Administração'}
                </h3>
                <Separator />

                {porEscopo[escopo].map((modulo) => {
                  const chaves = modulo.permissoes.map((p) => p.chave);
                  const todas = chaves.every((c) => marcadas.has(c));

                  return (
                    <div key={modulo.modulo} className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-foreground">{modulo.moduloLabel}</p>
                        <button
                          type="button"
                          onClick={() => alternarModulo(chaves, !todas)}
                          className="text-xs text-brand hover:underline"
                        >
                          {todas ? 'Desmarcar todas' : 'Marcar todas'}
                        </button>
                      </div>

                      <div className="flex flex-wrap gap-x-6 gap-y-2 pl-1">
                        {modulo.permissoes.map((permissao) => (
                          <div key={permissao.chave} className="flex items-center gap-2">
                            <Checkbox
                              id={permissao.chave}
                              checked={marcadas.has(permissao.chave)}
                              onCheckedChange={() => alternar(permissao.chave)}
                            />
                            <Label
                              htmlFor={permissao.chave}
                              className="text-sm font-normal cursor-pointer"
                            >
                              {permissao.acaoLabel}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </section>
            ))}
          </div>
        )}

        <DialogFooter>
          <div className="mr-auto">
            <Badge variant="secondary">{marcadas.size} marcadas</Badge>
          </div>
          <Button variant="outline" onClick={aoFechar} disabled={definir.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => void salvar()} disabled={definir.isPending || isPending}>
            {definir.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar permissões
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
